import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject, Subscriber, Subscription } from 'rxjs';
import { ApiService } from '../base/api.service';
import { ApiEndpointsService } from '../base/api-endpoints.service';
import { ServerResponse } from '../../interfaces/base/server.response';
import { Task } from '../../interfaces/task/task';
import { MenuCounts } from '../../interfaces/menu-counts';
import { Tile } from '../../interfaces/routering/tile';

@Injectable()

export class TaskService {

  public sourceUpdated: Subject<boolean> = new Subject<boolean>();
  public taskCreated: Subject<any> = new Subject<any>();
  public taskUpdated: Subject<any> = new Subject<any>();
  public taskDeletedId: Subject<number> = new Subject<number>();
  public taskOpenedId: Subject<number> = new Subject<number>();

  public listOfTaskIds: number[] = [];
  public listOfTaskIdsUpdated: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(private api: ApiService,
              private apiEndpoints: ApiEndpointsService,
  ) {
  }

  index(pageType: 'open' | 'control' | 'closed' | 'all' = 'open'): Observable<ServerResponse | boolean> {
    return new Observable<ServerResponse>((o: Subscriber<ServerResponse | boolean>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get(
        (pageType === 'open'
            ? 'tasks.open'
            : (pageType === 'closed'
                ? 'tasks.closed'
                : (pageType === 'control' ?
                    'tasks.control' :
                    'tasks.all'
                )
            )
        )
      ))
        .subscribe((response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(<ServerResponse>response);

            if (typeof response.data !== 'undefined') {
              this.listOfTaskIds = response.data.map((d: Task): number => d.id);
              this.listOfTaskIdsUpdated.next();
            }
          } else {
            o.next(false);
          }
        }, (): void => o.next(false));

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  missing(pageType: 'outside_of_files' | 'open' | 'control' | 'closed' | 'all' = 'open', start: number = 0): Observable<ServerResponse | boolean> {
    return new Observable<ServerResponse>((o: Subscriber<ServerResponse | boolean>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get(
        (
          (pageType === 'open'
              ? 'tasks.open'
              : (pageType === 'closed'
                  ? 'tasks.closed'
                  : (pageType === 'control'
                      ? 'tasks.control'
                      : 'tasks.all'
                  )
              )
          )
        )), {missing_start: start})
        .subscribe((response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(<ServerResponse>response);
          } else {
            o.next(false);
          }
        }, (): void => o.next(false));

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  count(): Observable<MenuCounts | boolean> {
    return new Observable<MenuCounts | boolean>((o: Subscriber<MenuCounts | boolean>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('tasks.count'), null, true)
        .subscribe((response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(<MenuCounts>response.data);
          } else {
            o.next(false);
          }
        }, (): void => o.next(false));

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  get(id: number): Observable<Task | boolean> {
    return new Observable<Task | boolean>((o: Subscriber<Task | boolean>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('tasks.read', {':id': id}))
        .subscribe((response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(<Task>response.data);
          } else {
            o.next(false);
          }
        }, (): void => o.next(false));

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  create(formData: any): Observable<ServerResponse> {
    return new Observable<ServerResponse>((o: Subscriber<ServerResponse>) => {
      const subscription: Subscription = this.api.post(this.apiEndpoints.get('tasks.create'), formData)
        .subscribe((response: ServerResponse): void => {
            if (response) {
              if (!this.api.isErrorResponse(response)) {
                this.taskCreated.next(response.data.feature);
              }
              o.next(response);
            } else {
              o.next(response);
            }
          },
          (): void => o.next());

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  update(id: number, formData: any): Observable<ServerResponse> {
    return new Observable<ServerResponse>((o: Subscriber<ServerResponse>) => {
      const subscription: Subscription = this.api.put(this.apiEndpoints.get('tasks.update', {':id': id}), formData)
        .subscribe((response: ServerResponse): void => {
            if (response) {
              this.taskUpdated.next(response.data.feature);
              o.next(response);
            } else {
              o.next(response);
            }
          },
          (): void => o.next());

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  handle(id: number, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.handle', {':id': id}), formData);
  }

  delete(id: number): Observable<boolean> {
    return new Observable<boolean>((o: Subscriber<boolean>) => {
      const subscription: Subscription = this.api.delete(this.apiEndpoints.get('tasks.delete', {':id': id}))
        .subscribe((response: ServerResponse): void => {
            if (response) {
              this.taskDeletedId.next(id);
              o.next(true);
            } else {
              o.next(false);
            }
          },
          (): void => o.next(false));

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  restore(id: number): Observable<boolean> {
    return new Observable<boolean>((o: Subscriber<boolean>) => {
      const subscription: Subscription = this.api.post(this.apiEndpoints.get('tasks.restore', {':id': id}))
        .subscribe((response: ServerResponse): void => {
            if (response) {
              o.next(true);
            } else {
              o.next(false);
            }
          },
          (): void => o.next(false));

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  export(pageType: string): Observable<ServerResponse> {
    return this.api.export(this.apiEndpoints.get('tasks.export', {':pageType': pageType}));
  }

  downloadResolutionNoteAttachment(id: number, resolution_note_id: number, attachment_id: number): void {
    this.api.download(this.apiEndpoints.get('tasks.download-resolution-note-attachment', {':id': id, ':resolution_note_id': resolution_note_id, ':attachment_id': attachment_id}));
  }

  deleteHandlingAttachment(id: number, resolution_note_id: number, attachment_id: number): Observable<boolean> {
    return new Observable<boolean>((o: Subscriber<boolean>) => {
      const subscription: Subscription = this.api.delete(this.apiEndpoints.get('tasks.download-resolution-note-attachment', {
        ':id': id,
        ':resolution_note_id': resolution_note_id,
        ':attachment_id': attachment_id
      }))
        .subscribe((response: ServerResponse): void => {
          if (response) {
            o.next(true);
          } else {
            o.next(false);
          }
        }, (): void => o.next(false));

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  createNote(id: number, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.notes.create', {':id': id}), formData);
  }

  updateNote(id: number, note_id: number, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.notes.update', {':id': id, ':note_id': note_id}), formData);
  }

  reassign(id: number, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.reassign', {':id': id}), formData);
  }

  assignToMyself(id: number): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.assign-to-myself', {':id': id}));
  }

  forward(id: number, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.forward', {':id': id}), formData);
  }

  reopen(id: number, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.reopen', {':id': id}), formData);
  }

  sendReminder(id: number, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.send-reminder', {':id': id}), formData);
  }

  history(id: number): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('tasks.history', {':id': id}));
  }

  rotatePhoto(id: number, photoId: number, direction: 'counter-clockwise' | 'clockwise'): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('tasks.rotate-photo', {':id': id, ':photoId': photoId, ':direction': direction}));
  }

  import(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.import'), formData);
  }

  importSave(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('tasks.import.save'), formData);
  }

  downloadExampleJson(): void {
    return this.api.download(this.apiEndpoints.get('tasks.import.download-example-json'));
  }

  handlingDaysRange(handling_days: number, remaining_days: number): Array<number> {
    const items: number[] = [0];
    if (handling_days !== null && handling_days > 0) {
      let p: number;
      if (remaining_days <= 0) {
        p = 1; // overdue (or exactly at deadline) → fully filled
      } else {
        p = (handling_days - remaining_days) / handling_days;
      }
      const p2 = Math.round(7 * Math.min(Math.max(p, 0), 1));
      for (let i = 1; i < p2; i++) {
        items.push(i);
      }
    }
    return items;
  }

  getPreviousId(id: number): number | null {
    const index: number = this.listOfTaskIds.indexOf(id);
    return index > 0 ? this.listOfTaskIds[index - 1] : null;
  }

  getNextId(id: number): number | null {
    const index: number = this.listOfTaskIds.indexOf(id);
    return index !== -1 && index < this.listOfTaskIds.length - 1 ? this.listOfTaskIds[index + 1] : null;
  }

  checkIfCoordsAreInsideTheBox(tile: Tile, coords: [number, number]): boolean {
    const polygon: [number, number][] = [
      [tile.topleft_lat, tile.topleft_lng],
      [tile.topright_lat, tile.topright_lng],
      [tile.bottomright_lat, tile.bottomright_lng],
      [tile.bottomleft_lat, tile.bottomleft_lng],
    ];

    return this.isPointInPolygon(coords, polygon);
  }

  private isPointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
    const [px, py] = point;
    let inside: boolean = false;
    const n: number = polygon.length;

    for (let i = 0, j = n - 1; i < n; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];

      const intersects =
        yi > py !== yj > py &&
        px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;

      if (intersects) {
        inside = !inside;
      }
    }

    return inside;
  }
}
